package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.groups.asGroupFailure
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.GroupFailure
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.GroupJoinDecision
import com.swetdot.swetgram.domain.GroupJoinRequestEntry
import com.swetdot.swetgram.domain.GroupJoinRequestId
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * A private group's approval queue: `listGroupJoinRequests` and `resolveGroupJoinRequest`.
 *
 * NEITHER DOCUMENT DESCRIBES THIS SURFACE. SPS §12.7's permission table has no join-request row,
 * §12.6's Members tab does not mention one, and STA §13.5 lists no join-request endpoint at all --
 * the contract has two and the folder has none. So the authority rule, the ordering, the
 * confirmations and the copy below are all DECISIONS, and each is grounded where it can be.
 *
 * AUTHORITY: Owner and Admin, derived from §12.7's removal row rather than declared. `Group.
 * hasJoinQueue` also hides the surface entirely for a public group -- the server answers an empty
 * page there rather than an error, because a public group joins immediately, but a screen that can
 * only ever be empty is not one worth navigating to.
 *
 * ORDER IS THE SERVER'S: newest first, off an index declared `(created_at DESC, id DESC)`. The
 * contract sets none, so nothing here re-sorts -- a client that did would interleave pages wrongly
 * and the cursor would stop meaning what the server thinks it means.
 *
 * ONE RESOLUTION AT A TIME, and that is not just a spinner-management convenience: two admins can
 * be looking at this queue, and an admin double-tapping Approve on a row that is already being
 * approved is the most likely way to discover that `resolveGroupJoinRequest` answers 204 for a
 * request that is no longer pending. Serialising them keeps the screen's story simple.
 *
 * NOTHING IS OPTIMISTIC. A row leaves the list when the server has answered, never before.
 * Approving somebody puts a stranger into a group's chat; showing that as done before it is done
 * would let two admins each believe they let a different person in.
 */

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

data class GroupJoinRequestsUiState(
    /**
     * The group, read alongside the first page.
     *
     * Needed for the permission re-check on every resolution, and for the header. Not passed in as
     * a navigation argument, where it would go stale the moment the viewer was demoted.
     */
    val group: Group? = null,
    val requests: List<GroupJoinRequestEntry> = emptyList(),
    val loadedOnce: Boolean = false,
    val loadingMore: Boolean = false,
    val hasMore: Boolean = false,
    /** The request currently being approved or rejected. Only ever one. */
    val resolving: GroupJoinRequestId? = null,
    val error: String? = null,
) {
    /**
     * Only pending rows are shown.
     *
     * The server pages an index partial on `status = 'pending'`, so this should never remove
     * anything -- but `GroupJoinRequestEntry.isPending` is false for a status this build could not
     * read, and offering Approve and Reject on a row whose state is unknown is the one thing a
     * moderation queue must not do.
     */
    val visible: List<GroupJoinRequestEntry> get() = requests.filter { it.isPending }

    /** False until the group is loaded, which grants nothing. */
    val canResolve: Boolean get() = group?.permissions?.canModerate == true
}

/**
 * Follows `GroupMembersViewModel`: a cursor-paginated list, the group read first, the cursor reset
 * on every refresh, and `CancellationException` rethrown before any other `Throwable`.
 */
class GroupJoinRequestsViewModel(
    private val groupId: GroupId,
    private val groups: GroupRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(GroupJoinRequestsUiState())
    val state: StateFlow<GroupJoinRequestsUiState> = _state.asStateFlow()

    /** Opaque and server-owned. Never parsed, compared or persisted. */
    private var cursor: String? = null

    private var paging = false
    private var loading = false

    init {
        refresh()
    }

    /** The Retry entry point: a fresh read that also takes the banner down. */
    fun refresh() = reload(clearError = true)

    /**
     * [clearError] is false after a failed resolution, which needs both the banner explaining the
     * refusal and a re-read of the queue. Clearing on the way in would erase the sentence the user
     * is being shown and the failure would look like a tap that did nothing.
     */
    private fun reload(clearError: Boolean) {
        if (loading) return
        loading = true
        if (clearError) _state.value = _state.value.copy(error = null)
        viewModelScope.launch {
            try {
                // The group first: without it the screen cannot decide whether this viewer may
                // resolve anything, and drawing the buttons before that is known flashes them in.
                val group = groups.group(groupId)
                val page = groups.joinRequests(groupId)
                cursor = page.nextCursor
                _state.value = _state.value.copy(
                    group = group,
                    requests = page.items,
                    hasMore = page.hasMore,
                    loadedOnce = true,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = error.groupBanner("Could not load the join requests."),
                )
            } finally {
                loading = false
            }
        }
    }

    /** Driven by the paging footer scrolling into view. */
    fun loadMore() {
        val current = _state.value
        if (paging || !current.hasMore) return
        val next = cursor ?: return
        paging = true
        _state.value = current.copy(loadingMore = true)
        viewModelScope.launch {
            try {
                val page = groups.joinRequests(groupId, cursor = next)
                cursor = page.nextCursor
                val held = _state.value.requests
                // De-duplicated by request id, and this is a crash guard rather than tidiness: the
                // list is keyed by that id and `LazyColumn` throws on two items with the same key.
                // A refresh landing while a page is in flight replaces the list under this append.
                val known = held.mapTo(mutableSetOf()) { it.id }
                _state.value = _state.value.copy(
                    requests = held + page.items.filterNot { it.id in known },
                    hasMore = page.hasMore,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // `hasMore` is left alone, so the end of the queue can be tried again.
                _state.value = _state.value.copy(
                    error = error.groupBanner("Could not load more join requests."),
                )
            } finally {
                paging = false
                _state.value = _state.value.copy(loadingMore = false)
            }
        }
    }

    /**
     * `resolveGroupJoinRequest`. 204 either way.
     *
     * The permission is re-checked here, against the group as last read, rather than trusted from a
     * button drawn a moment ago -- an admin can be demoted while this screen is open, and the guard
     * that does not depend on what was drawn is the only one worth having.
     *
     * AN APPROVAL CAN FAIL FOR REASONS THAT ARE ABOUT THE GROUP, NOT THE REQUEST, and each gets its
     * own sentence because "could not approve" would send the admin looking in the wrong place:
     * `409 group_full` means the group filled up while this request sat here (the cap is enforced on
     * approval, not when the request is filed), and `403 group_banned` means the applicant was
     * banned in the meantime. In both cases the row is dropped -- a request that cannot be approved
     * and whose subject is now barred is not a decision anybody is still waiting on -- except for
     * `group_full`, where the request is still genuinely pending and only the approval failed.
     *
     * A 204 FOR A REQUEST THAT WAS ALREADY RESOLVED is indistinguishable from a real one, so a
     * second admin's tap simply succeeds. That is the server's decision and the right one for a
     * queue; the cost is that a stale row disappears without saying who actually decided it.
     */
    fun resolve(requestId: GroupJoinRequestId, decision: GroupJoinDecision) {
        val current = _state.value
        if (current.resolving != null) return
        if (!current.canResolve) return
        val target = current.requests.firstOrNull { it.id == requestId && it.isPending } ?: return

        _state.value = current.copy(resolving = requestId, error = null)
        viewModelScope.launch {
            try {
                groups.resolveJoinRequest(groupId, requestId, decision)
                dropRequest(requestId)
                // The member count changed if this was an approval, and it is what the group screen
                // and the members header both show.
                if (decision == GroupJoinDecision.APPROVE) refreshGroupQuietly()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                val name = target.user.displayName
                when (error.asGroupFailure()) {
                    GroupFailure.GROUP_FULL -> _state.value = _state.value.copy(
                        resolving = null,
                        error = "This group is full, so $name cannot be approved yet. Their " +
                            "request stays here until somebody leaves.",
                    )
                    GroupFailure.BANNED -> {
                        _state.value = _state.value.copy(
                            resolving = null,
                            error = "$name is banned from this group and cannot be approved.",
                        )
                        dropRequest(requestId)
                    }
                    GroupFailure.INSUFFICIENT_ROLE -> {
                        _state.value = _state.value.copy(
                            resolving = null,
                            error = "You do not have permission to handle join requests here.",
                        )
                        // The role this screen was drawn from is not the server's; re-read without
                        // taking the banner down.
                        reload(clearError = false)
                    }
                    // `groupBanner` already has the sentence for a group that is gone, and for
                    // everything with no group meaning at all -- offline, a timeout, a 5xx.
                    else -> _state.value = _state.value.copy(
                        resolving = null,
                        error = error.groupBanner("Could not answer that request."),
                    )
                }
            }
        }
    }

    private fun dropRequest(requestId: GroupJoinRequestId) {
        _state.value = _state.value.copy(
            resolving = null,
            requests = _state.value.requests.filterNot { it.id == requestId },
        )
    }

    /**
     * Re-reads the group after an approval, for `member_count`.
     *
     * Swallowed on failure: the approval succeeded, and a banner about a count would report the
     * wrong thing as broken.
     */
    private fun refreshGroupQuietly() {
        viewModelScope.launch {
            runCatching { groups.group(groupId) }
                .onSuccess { _state.value = _state.value.copy(group = it) }
        }
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}
